/**
 * Google Enhanced Ecommerce Object
 *
 * Impressions logic and functionality for Google Enhanced Ecommerce.
 *
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const impressions = {

    /**
     * Product elements ordered by their state
     */
    elements: {
        unseen: [],
        inView: [],
        seen: [],
        queue: []
    },

    sendTimeout: null,
    watchingScroll: false,

    /**
     * Send details of any products/packages that has been seen by the user
     *
     * @param  {string}  productSelector  Optional selector to target specific products
     *  (e.g. ".newProducts[data-ecom-product]").
     * @returns  {void}
     */
    initialise: (productSelector) => {

        if (!gaEcommerce.core.checkStorageCapabilities()) return;

        if (typeof productSelector === 'undefined') {
            productSelector = '[data-ecom-product]';
        }

        const unseenProductElements = document.querySelectorAll(
            productSelector + ':not(.js-impression-seen)'
        );

        unseenProductElements.forEach((item) => {
            gaEcommerce.impressions.onUnseenElementFound(item);
        });
    },

    /**
     * When we find an unseen product element, we need to add it to the list
     *
     * @param {object} productElement DOM element
     * @returns {void}
     */
    onUnseenElementFound: (productElement) => {
        // Double check that we haven't seen this element yet
        if (gaEcommerce.impressions.elements.seen.indexOf(productElement) >= 0
            || gaEcommerce.impressions.elements.unseen.indexOf(productElement) >= 0
            || productElement.classList.contains('js-impression-unseen')) {
            return;
        }

        productElement.classList.add('js-impression-unseen');
        gaEcommerce.impressions.elements.unseen.push(productElement);

        let portion = 0.75;

        if (productElement.dataset.ecomViewType === 'detail') {
            portion = 0.01;
        }

        if (gaEcommerce.util.isSeenInRange(
            gaEcommerce.impressions.getImpressionTargetElement(productElement),
            gaEcommerce.util.getViewport(),
            portion
        )) {
            gaEcommerce.impressions.onElementInViewPort(productElement);
        }

        if (!gaEcommerce.impressions.watchingScroll) {
            gaEcommerce.impressions.watchingScroll = true;
            window.addEventListener('scroll', gaEcommerce.impressions.getThrottledListener());
            window.addEventListener('resize', gaEcommerce.impressions.getThrottledListener());
            const observer = new MutationObserver(gaEcommerce.impressions.getThrottledListener()),
                  options = {
                      attributeFilter: ["class", "style"],
                      attributes: true,
                      childList: true,
                      subtree: true
                  };

            observer.observe(document.getElementsByTagName('body')[0], options);
        }
    },

    /**
     * When an element is in the viewport, we need to ensure it stays their for 0.5s before marking it as seen
     *
     * @param {object} element DOM element
     * @returns {void}
     */
    onElementInViewPort: (productElement) => {
        // Already seen or monitoring this element, so just bail
        if (gaEcommerce.impressions.elements.seen.indexOf(productElement) >= 0
            || gaEcommerce.impressions.elements.inView.indexOf(productElement) >= 0) {
            return;
        }

        gaEcommerce.impressions.elements.inView.push(productElement);

        let portion = 0.75;
        if (productElement.dataset.ecomViewType === 'detail') {
            portion = 0.01;
        }

        setTimeout(() => {
            if (gaEcommerce.util.isSeenInRange(
                gaEcommerce.impressions.getImpressionTargetElement(productElement),
                gaEcommerce.util.getViewport(),
                portion
            )) {
                gaEcommerce.impressions.onElementSeen(productElement);
            }
            gaEcommerce.impressions.removeFromInView(productElement);
        }, 500);
    },

    /**
     * The element has now been seen, so we need to tell GA
     *
     * @param {object} element DOM element
     * @returns {void}
     */
    onElementSeen: (element) => {
        // If it's already been seen or on the queue
        if (gaEcommerce.impressions.elements.seen.indexOf(element) >= 0
            || gaEcommerce.impressions.elements.queue.indexOf(element) >= 0
            || element.classList.contains('js-impression-seen')) {
            return;
        }

        element.classList.add('js-impression-seen');

        gaEcommerce.impressions.elements.seen.push(element);
        gaEcommerce.impressions.removeFromUnseen(element);

        gaEcommerce.impressions.elements.queue.push(element);

        if (gaEcommerce.impressions.sendTimeout === null) {
            gaEcommerce.impressions.sendTimeout = setTimeout(gaEcommerce.impressions.send, 250);
        }
    },

    /**
     * Send batch to GA
     *
     * @returns {void}
     */
    send: () => {

        const toSend = gaEcommerce.impressions.elements.queue;

        if (toSend.length < 1) {
            return;
        }

        gaEcommerce.impressions.elements.queue = [];
        gaEcommerce.impressions.sendTimeout = null;

        gaEcommerce.impressions.sendImpressions(toSend);
    },

    /**
     * Remove from the inView list
     *
     * @param {object} element DOM element
     * @returns {void}
     */
    removeFromInView: (element) => {
        gaEcommerce.util.removeFromList(element, gaEcommerce.impressions.elements.inView);
    },

    /**
     * Remove from the unseen list
     *
     * @param {object} element DOM element
     * @returns {void}
     */
    removeFromUnseen: (element) => {
        gaEcommerce.util.removeFromList(element, gaEcommerce.impressions.elements.unseen);
    },

    /**
     * Send impressions to GA
     *
     * @param {array} productElements
     * @returns {void}
     */
    sendImpressions: (productElements) => {

        const ecomObject = {},
              sanitisedProducts = gaEcommerce.impressions.sanitiseProducts(productElements);

        if (
            sanitisedProducts.listImpressions.length < 1
            && sanitisedProducts.detailImpressions.length < 1
        ) {
            return;
        }

        // Build the ecomObject and send it to GA
        if (sanitisedProducts.listImpressions.length) {
            ecomObject.impressions = sanitisedProducts.listImpressions;
        }

        if (sanitisedProducts.detailImpressions.length) {
            ecomObject.detail = {};
            ecomObject.detail.products = sanitisedProducts.detailImpressions;
        }

        gaEcommerce.core.sendData(ecomObject, 'impressions');
    },

    /**
     * Validate and sanitise objects, sorting them into listImpressions or detailImpressions arrays
     *
     * @param {array} productElements
     * @returns {{listImpressions: Array, detailImpressions: Array}}
     */
    sanitiseProducts: (productElements) => {

        const listImpressions = [],
              detailImpressions = [];

        productElements.forEach((element) => {
            const rawProduct = gaEcommerce.util.parseJsonObj(element.dataset.ecomProduct),
                  sanitisedProduct = gaEcommerce.core.validateProduct(rawProduct);

            // If the product is not valid, just skip it
            if (sanitisedProduct === false) {
                return true;
            }

            const productData = gaEcommerce.core.includeDynamicProductProperties(
                element,
                sanitisedProduct,
                {quantity: false}
            );

            // Add this item to impressions array...
            if (element.dataset.ecomViewType === 'detail') {
                detailImpressions.push(productData);
            } else {
                listImpressions.push(productData);
            }
        });

        return {
            listImpressions: listImpressions,
            detailImpressions: detailImpressions
        };
    },

    /**
     * Watch for products entering the viewport
     *
     * @returns {function}
     */
    getThrottledListener: () => (
        gaEcommerce.util.throttle(() => {
            if (gaEcommerce.impressions.elements.unseen.length < 1) {
                return;
            }

            const viewport = gaEcommerce.util.getViewport();

            gaEcommerce.impressions.elements.unseen.map((productElement) => {
                let portion = 0.75;
                if (productElement.dataset.ecomViewType === 'detail') {
                    portion = 0.01;
                }

                if (gaEcommerce.util.isSeenInRange(
                    gaEcommerce.impressions.getImpressionTargetElement(productElement),
                    gaEcommerce.util.getViewport(),
                    portion
                )) {
                    gaEcommerce.impressions.onElementInViewPort(productElement);
                }
            });
        })
    ),
    
    /*
     * Target either the product or a sub element
     * that contains the following class '.js-impression-trigger'
     *
     * @param {object} productElement DOM element
     * @return {object} DOM element
     */
    getImpressionTargetElement: (productElement) => 
        productElement.querySelector('.js-impression-trigger') || productElement
};

export default impressions;
